// src/api/axiosInstance.js
import axios from 'axios';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else       prom.resolve(token);
  });
  failedQueue = [];
};

// ✅ الدالة المعدلة: تطلق حدثاً بدلاً من إعادة تحميل الصفحة
const clearAuthAndRedirect = () => {
  window.dispatchEvent(new Event('unauthorized_access'));
};

// ─── Request Interceptor ─────────────────────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    const token    = localStorage.getItem('access_token');
    const authType = localStorage.getItem('auth_type');

    if (token) {
      // ✅ الزبون → Token Auth | الموظف → Bearer JWT
      config.headers['Authorization'] =
        authType === 'client' ? `Token ${token}` : `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor ────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // الزبون لا يملك refresh token — اخرج مباشرة عند 401
    const authType = localStorage.getItem('auth_type');
    if (authType === 'client') {
      if (error.response?.status === 401) {
        clearAuthAndRedirect();
      }
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/token/refresh/`,
        { refresh: refreshToken }
      );
      const newAccessToken = response.data.access;
      localStorage.setItem('access_token', newAccessToken);
      originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
      processQueue(null, newAccessToken);
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearAuthAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;